import type { PricingProvider, ProductModel } from '@ox/schema';

export type TemplateVariantDraft = {
  size: string;
  color: string;
  catalogRef: string;
  costAmount: number;
  currency: string;
};

/**
 * Build template variants from the loaded product model + member quote.
 * Cost = member/catalog unit (via quote) + print extras for used views.
 * Retail is derived later from marginPercent — do not bake retail here.
 */
export async function buildTemplateVariantsFromModel(
  model: ProductModel,
  pricing: PricingProvider,
  designId: string,
): Promise<TemplateVariantDraft[]> {
  const list = (model.variants ?? []).slice(0, 40);
  if (!list.length) {
    throw new Error('Este producto no tiene variantes (tallas/colores)');
  }

  const colorLabel = (key: string) =>
    model.colors.find((c) => c.key === key)?.label || key;
  const sizeLabel = (id: string) =>
    model.sizes.find((s) => s.id === id)?.label || id;

  const drafts: TemplateVariantDraft[] = list.map((v) => ({
    size: sizeLabel(v.sizeId),
    color: colorLabel(v.colorKey),
    catalogRef: v.id,
    costAmount: typeof v.price?.amount === 'number' ? v.price.amount : 0,
    currency: v.price?.currency || 'PEN',
  }));

  const quoteable = drafts.filter((v) => v.catalogRef.startsWith('woo:'));
  if (!quoteable.length) {
    return drafts;
  }

  const quote = await pricing.quote({
    lines: quoteable.map((v) => ({ variantId: v.catalogRef, quantity: 1 })),
    designId: designId && designId !== 'draft' ? designId : undefined,
  });
  const byId = new Map(quote.lines.map((l) => [l.variantId, l]));
  const extrasUnit = Number(quote.extrasUnit?.amount ?? 0) || 0;

  return drafts.map((v) => {
    const line = byId.get(v.catalogRef);
    const base = line?.unitPrice.amount ?? v.costAmount;
    const cost = Math.round((Number(base) + extrasUnit) * 100) / 100;
    const currency = line?.unitPrice.currency ?? v.currency;
    return { ...v, costAmount: cost, currency };
  });
}
